import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { BaseModal } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import type { Movimentacao, Solicitacao } from '../../../../models';
import {
  MOMENTO_EMAIL_CLIENTE_LABELS,
  VARIAVEIS_EMAIL_CLIENTE,
  montarLinkEmail,
  preencherEmailCliente,
  variaveisUsadas,
} from '../../../../models/emailCliente.modelos';
import type { ModeloEmailCliente } from '../../../../models/emailCliente.modelos';
import { useSalvarDadosClienteMutation } from '../../../../hooks/data';
import { comentarioSolicitacaoService } from '../../../../services';
import { useValoresEmailCliente } from './useValoresEmailCliente';

interface PrepararEmailClienteModalProps {
  modelo: ModeloEmailCliente | null;
  solicitacao: Solicitacao;
  movimentacoes: Movimentacao[];
  onClose: () => void;
}

/**
 * Prévia do e-mail para o Cliente com cada dado editável. "Abrir no Outlook" monta o mailto
 * (sai da caixa de quem envia), grava o que foi completado do bloco "Dados para o cliente" e
 * registra uma postagem nas Observações. Os dados pessoais corrigidos aqui valem só pro e-mail
 * — não mudam o cadastro.
 */
export default function PrepararEmailClienteModal({ modelo, solicitacao, movimentacoes, onClose }: PrepararEmailClienteModalProps) {
  const { user } = useAuth();
  const toast = useToast();
  const salvarDadosCliente = useSalvarDadosClienteMutation(solicitacao.id);
  const { valores: calculados, avisos } = useValoresEmailCliente(solicitacao, movimentacoes, modelo);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [abrindo, setAbrindo] = useState(false);

  // Recarrega quando os dados chegam (cadastro/pré-cadastro são buscados ao abrir).
  useEffect(() => setValores(calculados), [calculados]);

  const campos = useMemo(() => {
    if (!modelo) return [];
    const usadas = variaveisUsadas([modelo.assunto, modelo.corpo]);
    return VARIAVEIS_EMAIL_CLIENTE.filter((v) => usadas.has(v.chave) && v.chave !== 'cliente');
  }, [modelo]);

  if (!modelo) return null;

  const assunto = preencherEmailCliente(modelo.assunto, valores);
  const corpo = preencherEmailCliente(modelo.corpo, valores);
  const faltando = campos.filter((c) => !valores[c.chave]?.trim());

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(corpo);
      toast.success('Texto do e-mail copiado.');
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  const abrirOutlook = async () => {
    setAbrindo(true);
    try {
      const daAbertura = campos.filter((c) => c.origem === 'abertura');
      const dadosCliente = { ...(solicitacao.dadosCliente ?? {}) };
      daAbertura.forEach((c) => {
        const valor = valores[c.chave]?.trim();
        if (valor) dadosCliente[c.chave] = valor;
      });
      if (JSON.stringify(dadosCliente) !== JSON.stringify(solicitacao.dadosCliente ?? {})) {
        await salvarDadosCliente.mutateAsync(dadosCliente);
      }
      if (user?.collaboratorId && user.email) {
        const destino = [modelo.para.join(', '), modelo.cc.length ? `cópia: ${modelo.cc.join(', ')}` : null].filter(Boolean);
        await comentarioSolicitacaoService
          .create(
            solicitacao.id,
            `📧 E-mail "${MOMENTO_EMAIL_CLIENTE_LABELS[modelo.momento]} · ${modelo.clienteNome}" preparado no Outlook para ${destino.join(' · ')}.`,
            { id: user.collaboratorId, name: user.displayName ?? user.email, avatar: user.photoURL ?? null },
            user.email
          )
          .catch((error) => console.error('Erro ao registrar o e-mail nas Observações:', error));
      }
      window.location.href = montarLinkEmail(modelo.para, modelo.cc, assunto, corpo);
      onClose();
    } finally {
      setAbrindo(false);
    }
  };

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title={`E-mail · ${MOMENTO_EMAIL_CLIENTE_LABELS[modelo.momento]} · ${modelo.clienteNome}`}
      maxWidth="max-w-4xl"
      actions={
        <div className="flex flex-wrap items-center justify-end gap-3">
          {faltando.length > 0 && (
            <p className="mr-auto text-xs text-amber-700">Sem valor: {faltando.map((c) => c.label).join(', ')}</p>
          )}
          <button type="button" className="btn-secondary" onClick={copiar}>
            Copiar texto
          </button>
          <button type="button" className="btn-primary" disabled={abrindo} onClick={abrirOutlook}>
            {abrindo ? 'Abrindo...' : 'Abrir no Outlook'}
          </button>
        </div>
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <div className="space-y-1 text-xs text-gray-500">
            <p>Confira os dados antes de abrir o e-mail. Campo vazio fica com borda laranja.</p>
            <p>
              <span className="font-semibold text-amber-800">Amarelos</span>: informados por quem abriu a Solicitação. Se você
              corrigir, a Solicitação é atualizada.
            </p>
            <p>
              <span className="font-semibold text-gray-700">Brancos</span>: vêm do cadastro do colaborador. Se você corrigir, muda só
              neste e-mail — o cadastro não é alterado.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {campos.map((campo) => (
              <label key={campo.chave} className={clsx('block text-xs font-medium', campo.origem === 'abertura' ? 'text-amber-800' : 'text-gray-700')}>
                {campo.label}
                <input
                  className={clsx(
                    'input-primary mt-0.5 w-full text-sm',
                    campo.origem === 'abertura' && 'bg-amber-50/60',
                    !valores[campo.chave]?.trim() && 'border-amber-400'
                  )}
                  value={valores[campo.chave] ?? ''}
                  onChange={(event) => setValores((atual) => ({ ...atual, [campo.chave]: event.target.value }))}
                />
                {avisos[campo.chave] && <span className="mt-0.5 block font-normal text-amber-700">⚠ {avisos[campo.chave]}</span>}
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-2 text-[13px]">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-gray-900">
            <p className="text-xs text-gray-500">Para: {modelo.para.join(', ')}</p>
            {modelo.cc.length > 0 && <p className="text-xs text-gray-500">Cópia: {modelo.cc.join(', ')}</p>}
            <p className="mt-2 border-b border-gray-200 pb-2 font-semibold">{assunto}</p>
            <p className="whitespace-pre-wrap pt-2 leading-relaxed">{corpo}</p>
          </div>
          <p className="text-[11px] text-gray-500">
            Abre no seu Outlook com tudo preenchido — o envio é feito por você, da sua caixa.
          </p>
        </div>
      </div>
    </BaseModal>
  );
}
